"use client";

import { SearchDialog, type SearchDialogGroup } from "@/components/search-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SquarePenIcon } from "@/components/ui/square-pen";
import { WrenchIcon } from "@/components/ui/wrench";
import type { DbTool, ToolScope } from "@/types/db";
import { ZapIcon } from "lucide-react";
import { useRouter } from "next/navigation";

// ─── Scope badge ──────────────────────────────────────────────────────────────

const SCOPE_LABEL: Record<ToolScope, string> = {
  global: "Global",
  team: "Team",
  user: "User",
};

const SCOPE_ORDER: ToolScope[] = ["global", "team", "user"];

const ScopeBadge = ({ scope }: { scope: ToolScope }) => (
  <span className="ml-auto shrink-0 rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
    {SCOPE_LABEL[scope]}
  </span>
);

// ─── Main dialog ──────────────────────────────────────────────────────────────

type ToolsBrowseDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tools: DbTool[];
  onEdit: (tool: DbTool) => void;
  onCreateTool?: () => void;
  isSuperAdmin?: boolean;
};

export function ToolsBrowseDialog({
  open,
  onOpenChange,
  tools,
  onEdit,
  onCreateTool,
  isSuperAdmin = false,
}: ToolsBrowseDialogProps) {
  const router = useRouter();

  const groups: SearchDialogGroup[] = (() => {
    const result: SearchDialogGroup[] = [];

    // Built-in group
    const builtins = tools.filter((t) => t.source_type === "builtin");
    if (builtins.length > 0) {
      result.push({
        heading: "Built-in",
        items: builtins.map((tool) => ({
          id: tool.id,
          value: [tool.name, tool.description].filter(Boolean).join(" "),
          label: tool.name,
          icon: <ZapIcon className="size-4 shrink-0 text-muted-foreground" />,
          meta: (
            <span className="ml-auto flex shrink-0 items-center gap-1.5">
              {!tool.enabled && (
                <Badge variant="outline" className="px-1.5 py-0 text-[10px] text-muted-foreground">
                  Disabled
                </Badge>
              )}
              <ScopeBadge scope={tool.scope} />
              {isSuperAdmin && (
                <Button
                  aria-label={`Edit builtin tool ${tool.name}`}
                  className="size-6 p-0 text-muted-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenChange(false);
                    router.push(`/tools/${tool.id}/edit`);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <SquarePenIcon size={14} />
                </Button>
              )}
            </span>
          ),
        })),
      });
    }

    // Custom / internal tools grouped by scope
    const custom = tools.filter((t) => t.source_type !== "builtin");
    const byScope = new Map<ToolScope, DbTool[]>();
    for (const tool of custom) {
      if (!byScope.has(tool.scope)) byScope.set(tool.scope, []);
      byScope.get(tool.scope)!.push(tool);
    }

    for (const scope of SCOPE_ORDER) {
      const items = byScope.get(scope);
      if (!items?.length) continue;

      result.push({
        heading: SCOPE_LABEL[scope],
        items: items.map((tool) => ({
          id: tool.id,
          value: [tool.name, tool.description, ...tool.teams.map((w) => w.name)]
            .filter(Boolean)
            .join(" "),
          label: tool.name,
          icon: <WrenchIcon size={16} className="shrink-0 text-muted-foreground" />,
          meta: (
            <span className="ml-auto flex shrink-0 items-center gap-1.5">
              <ScopeBadge scope={tool.scope} />
              <Button
                aria-label={`Edit ${tool.name}`}
                className="size-6 p-0 text-muted-foreground"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEdit(tool);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                size="icon"
                type="button"
                variant="ghost"
              >
                <SquarePenIcon size={14} />
              </Button>
            </span>
          ),
        })),
      });
    }

    return result;
  })();

  return (
    <>
      <SearchDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Tools"
        description="Browse and manage tools"
        placeholder="Search tools…"
        emptyMessage="No tools found."
        groups={groups}
        onSelect={(id) => {
          const tool = tools.find((t) => t.id === id);
          if (!tool) return;
          if (tool.source_type === "builtin") {
            if (isSuperAdmin) { onOpenChange(false); router.push(`/tools/${tool.id}/edit`); }
          } else {
            onEdit(tool);
          }
        }}
        onCreate={onCreateTool}
        createLabel="tool"
      />
    </>
  );
}
